import { useEffect, useState } from 'react'
import { t } from 'ttag'
import _debug from 'debug'

import './StorageLimitStatus.css'
import { HardDriveIcon } from '../utils/Icons'
import { COMPRESSION_SERVER_ORIGIN } from '../utils/VideoCompressor'
import { logError, sendBeacon } from '../utils/Errors'
import { doOnceWhenBackOnline } from '../utils/ServiceStatus'

const log = _debug('sltt:StorageLimitStatus')

const GIGABYTE = 1024 * 1024 * 1024
const MIN_STORAGE_GB = 2

let hasReportedToRollbar = false
let hasReportedErrorToRollbar = false

async function checkStorage() {
    try {
        // Call the compressor's Free Space API
        const freeSpaceResponse = await fetch(`${COMPRESSION_SERVER_ORIGIN}/freeSpace`)
        if (!freeSpaceResponse.ok) {
            const errorStatusMsg = `## Failed to fetch free space: ${freeSpaceResponse.status} - ${freeSpaceResponse.statusText}`
            log(errorStatusMsg)
            throw new Error(errorStatusMsg)
        }
        const freeSpace = await freeSpaceResponse.json()
        log('Free Space:', freeSpace.free)

        // Perform the calculation directly
        const remainingStorageGB = freeSpace.free / GIGABYTE // Convert free space to GB
        const isStorageLow = remainingStorageGB < MIN_STORAGE_GB // Determine if storage is low
        const messageLowStorage = t`Low available computer storage: ` + remainingStorageGB.toFixed(1) + ' ' +  t`gigabytes (GB).` + `<br>`
        const message = t`Please free up at least 2 gigabytes(GB) of computer storage.` + `<br>` + messageLowStorage
        log(`Is storage low: ${isStorageLow}`)
        if (isStorageLow) {
            // Rollbar reporting
            console.warn(`Remaining computer storage: ${remainingStorageGB.toFixed(1)} gigabytes (GB).`)
            if (!hasReportedToRollbar) {
                doOnceWhenBackOnline(`send beacon for low storage status`,
                    () => {
                        sendBeacon('low-storage-status', { remainingStorageGB })
                    }
                )
                hasReportedToRollbar = true
            }
        }
        return { message, isStorageLow }
    } catch (error) {
        if ((error as Error).message === 'Failed to fetch') {
            const compressorMsg = 'Compressor is not running:'
            // log(compressorMsg, error)
            return { message: compressorMsg, isStorageLow: false }
        } else {
            // Rollbar reporting
            const rollbarMsg = 'Storage Limit Status compressor server error:'
            console.error(rollbarMsg, error)
            if (!hasReportedErrorToRollbar) {
                doOnceWhenBackOnline(`reporting compressor server error to rollbar`,
                    () => {
                        logError(error, rollbarMsg)
                    }
                )
                hasReportedErrorToRollbar = true
            }
            return { message: rollbarMsg, isStorageLow: false }
        }
    }
}

export const StorageLimitStatusCheck = () => {  
    const [isStorageLow, setIsStorageLow] = useState<boolean | undefined>(false)
    const [tooltip, setTooltip] = useState('')
    
    useEffect(() => {
        // Define an async function inside the useEffect
        const fetchStorage = async () => {
            const storage = await checkStorage()
            setTooltip(storage.message)
            setIsStorageLow(storage.isStorageLow)
        }
        // Immediately call the async function
        fetchStorage()

        // Set up the interval
        const intervalId = setInterval(() => {
            fetchStorage()
        }, 60000) // Check storage every 1 minute

        // Clean up the interval on component unmount
        return () => clearInterval(intervalId)
    }, [])

    return (
        <span>
            {isStorageLow && (
                <HardDriveIcon id={"storage-limit-status-icon"} 
                    className={`storage-limit-status-icon ${isStorageLow ? 'storage-limit-status-low' : ''}`}
                    tooltip={tooltip} />
            )}
        </span>
    )
}
